import { LazyQueryExecFunction, useLazyQuery } from '@apollo/client';
import {
	CreateRedditUserMutationCreateUser,
	RedditUsersListDocument, RedditUsersListQuery,
	RedditUsersListQueryVariables,
	UpdateRedditUserMutationUpdateUser,
} from 'wb-reddit/graphql/generated';
import {
	useMemo, useState, useCallback,
} from 'react';
import { RedditHeaderSearch } from 'wb-reddit/app/common/components/headerSearch';
import { debounce } from 'lodash';
import {
	Button, GetRowsParams, InfiniteScrollList, PlusIcon, UserIcon, isReddit,
} from '@aleph/shared-components';
import RedditNotifications from 'wb-reddit/app/common/components/notifications';
import useRedditUserMutationStorage, {
	newAuthGraphUserData,
	newUserData,
} from 'wb-reddit/app/routes/users/mutator/useRedditUserMutationStorage';
import RedditUsersMutatorContainer from 'wb-reddit/app/routes/users/mutator/container';
import useUserMetaData from 'wb-reddit/app/common/hooks/useUserMetaData';
import RedditUserListItem from 'wb-reddit/app/routes/users/components/user';
import RedditUserLoader from 'wb-reddit/app/routes/users/components/loader';
import { useLayoutRef } from 'wb-reddit/app/routes/context/layoutContext';
import useUpdateRows, { HandleUpdateRow } from 'wb-reddit/app/routes/hooks/useUpdateRows';
import { useTranslation } from 'react-i18next';
import {
	AuthGraphUsersListDocument,
	AuthGraphUsersListQuery,
	AuthGraphUsersListQueryVariables,
	UserGroupType,
} from 'wb-spotify/graphql/authgraph/generated';
import useSpotifyRolePermissions from 'wb-spotify/app/common/hooks/useSpotifyPermissions';

export type UserRow = UpdateRedditUserMutationUpdateUser | CreateRedditUserMutationCreateUser;

export type HandleUpdateUserRow = HandleUpdateRow<UserRow>;

const RedditUsersContainer = () => {
	const { t } = useTranslation();
	const [search, setSearch] = useState<string>('');
	const { mutationUser, setMutationUser } = useRedditUserMutationStorage();

	const debouncedSearch = useMemo(() => debounce(setSearch, 500), []);

	const [getUsers] = useLazyQuery(isReddit()
		? RedditUsersListDocument
		: AuthGraphUsersListDocument);

	// @ts-ignore
	const getRedditUsers = getUsers as LazyQueryExecFunction<RedditUsersListQuery, RedditUsersListQueryVariables>;

	const platformUser = useUserMetaData(import.meta.env.VITE_PACKAGE_APP);

	const getRows = useCallback(async (params: GetRowsParams<string | null>) => {
		if (isReddit()) {
			const { data } = await getRedditUsers({
				variables: {
					cursor: params.metaData ?? undefined,
					search: search ? `*${search}*` : '**',
				},
				fetchPolicy: 'network-only',
			});

			return {
				data:        data?.users.items ?? [],
				hasNextPage: !!data?.users.cursor,
				metaData:    data?.users.cursor ?? null,
			};
		}

		const getAuthGraphUsers = getUsers as LazyQueryExecFunction<AuthGraphUsersListQuery, AuthGraphUsersListQueryVariables>;

		const { data } = await getAuthGraphUsers({
			variables: {
				cursor: params.metaData ?? undefined,
				search: search ? `*${search}*` : '**',
				email:  '**',
			},
			fetchPolicy: 'network-only',
		});

		return {
			data: (data?.users?.items ?? []).map(
				user => ({
					...user,
					// @ts-ignore
					userGroups: user.userGroupAccess
						?.filter(group => group?.userGroup?.userGroupType === UserGroupType.Public && group?.userGroup?.userGroupName)
						?.map(group => ({
							...group.userGroup,
							userGroupName:       group.userGroup?.userGroupName as string,
							userGroupAccessRole: group.userGroupAccessRole,
						})),
				// Omitting account level from type because the difference is in enums, should be fine
				} satisfies Omit<UserRow, 'userAccountLevel'>),
			),
			hasNextPage: !!data?.users?.cursor,
			metaData:    data?.users?.cursor ?? null,
		};
	}, [search]);

	const parentElement = useLayoutRef();

	const {
		handleUpdateRow,
		exposedMethods,
	} = useUpdateRows<UserRow>();

	const { canCreateUser: spotifyCanCreateUser } = useSpotifyRolePermissions();

	const canCreateUser = useMemo(() => {
		if (isReddit()) {
			return platformUser?.isAdmin;
		}

		return spotifyCanCreateUser;
	}, [platformUser, spotifyCanCreateUser]);

	return (
		<>
			{mutationUser && <RedditUsersMutatorContainer updateRow={handleUpdateRow} />}

			<div className="flex flex-col gap-[16px] w-full sticky top-0 left-0 bg-gray-0 z-10">
				<div
					className="flex h-[64px] items-center justify-between border-b border-b-black"
					data-testid="users-header-container"
				>
					<div className="flex flex-1 items-center gap-[16px] h-full pr-[16px]
						text-[18px] text-gray-800 font-medium"
					>
						<UserIcon
							width={24}
							height={24}
						/>
						{t('sc: users', 'Users')}
					</div>

					<RedditHeaderSearch
						search={search}
						setSearch={debouncedSearch}
					/>

					<div className="pl-[24px] h-full flex items-center">
						<RedditNotifications />
					</div>
				</div>

				{
					canCreateUser
					&& <div>
						<Button
							title={t('sc: new', 'New')}
							leftNode={<PlusIcon />}
							onClick={
								() => setMutationUser(
									isReddit()
										? newUserData
										: newAuthGraphUserData,
								)
							}
						/>
					</div>
				}

				<div className="flex items-center mb-[-16px] h-[36px]
				text-black font-bold border-b border-gray-200 bg-gray-0"
				>
					<div className="min-w-[250px] flex-1 px-[8px]">
						{t('sc: filters.entity.user', 'User')}
					</div>

					<div className="w-[250px] px-[8px]">
						{t('re: email', 'E - Mail')}
					</div>

					<div className="w-[215px] px-[8px]">
						{t('sc: role', 'Role')}
					</div>

					<div className="w-[165px] px-[8px]">
						{t('sc: access', 'Access')}
					</div>

					<div className="w-[170px] px-[12px]">
						{t('sc: teams', 'Teams')}
					</div>

					<div className="w-[70px]" />
				</div>
			</div>
			<InfiniteScrollList<string | null, UserRow>
				className='w-full'
				parentElement={parentElement?.current}
				// @ts-ignore TODO fix
				getRows={getRows}
				exposedMethods={exposedMethods}
				renderItem={({ props, data, isLoaderRow }) => {
					if (isLoaderRow) {
						return <RedditUserLoader key={props.key} />;
					}

					return (<RedditUserListItem
						{...props}
						deleteRow={handleUpdateRow}
						user={data}
					/>);
				}}
			/>

		</>
	);
};

export default RedditUsersContainer;
